import type { ReactNode } from "react";
import React, { useEffect, useState } from "react";
import classNames from "classnames";
import type { IconType } from "@circle-react-shared/Icon";
import { RadioButton } from "./RadioButton";

export interface Option {
  icon?: IconType;
  iconSize?: number;
  label: ReactNode | string;
  value: string;
}

export interface RadioButtonGroupProps {
  checkedOptionClassName?: string;
  defaultChecked: string;
  hasCheckedIcon?: boolean;
  hasRadioCheckmarks?: boolean;
  name: string;
  onChange?: (value: any) => void;
  options: Option[];
  Separator?: ReactNode;
  shouldShowInlineOptions?: boolean;
  size?: "sm" | "md";
  uncheckedOptionClassName?: string;
  wrapperClassName?: string;
}

export const RadioButtonGroup = ({
  name,
  options,
  defaultChecked,
  onChange,
  size = "md",
  hasRadioCheckmarks = false,
  hasCheckedIcon = true,
  shouldShowInlineOptions = false,
  checkedOptionClassName = "",
  uncheckedOptionClassName = "",
  Separator,
  wrapperClassName,
}: RadioButtonGroupProps) => {
  const [checkedOption, setCheckedOption] = useState(defaultChecked);

  useEffect(() => {
    if (checkedOption && onChange) {
      onChange(checkedOption);
    }
  }, [onChange, checkedOption]);

  return (
    <div
      className={classNames(
        "flex",
        {
          "flex-col gap-3": !shouldShowInlineOptions,
          "flex-row justify-center gap-4 text-center": shouldShowInlineOptions,
        },
        wrapperClassName,
      )}
    >
      {options.map(({ value, label, icon, iconSize }, index) => (
        <React.Fragment key={value}>
          <RadioButton
            data-testid={`radio-button-group-${index}`}
            isChecked={value === checkedOption}
            onChange={setCheckedOption}
            label={label}
            name={name}
            id={`${name}-${index}`}
            value={value}
            size={size}
            hasRadioCheckmarks={hasRadioCheckmarks}
            hasCheckedIcon={hasCheckedIcon}
            icon={icon}
            iconSize={iconSize}
            checkedOptionClassName={checkedOptionClassName}
            uncheckedOptionClassName={uncheckedOptionClassName}
          />
          {index < options.length - 1 && Separator}
        </React.Fragment>
      ))}
    </div>
  );
};
